import { useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import HubIcon from '@mui/icons-material/Hub';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InsightsIcon from '@mui/icons-material/Insights';
import RedeemIcon from '@mui/icons-material/Redeem';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import { useAdminSolicitarAcessoMutation } from '../../../api';
import { formatarBRL } from '../../../lib/formato';
import { AoEntrar, CartaoPonto, Grade, PainelDestaque } from './comuns';

/** Módulo 76.2 do Sischef — identifica a empresa matriz de uma franquia. */
const ROLE_FRANQUEADOR = 'ROLE_FRANQUEADOR';

/** Números do exemplo. Concretos de propósito: é o argumento comercial. */
const LOJAS_EXEMPLO = 12;
const CUPOM_EXEMPLO = 20;

const VANTAGENS: { icon: JSX.Element; titulo: string; texto: string }[] = [
  {
    icon: <LocalOfferIcon />,
    titulo: 'Um cupom, todas as lojas',
    texto:
      'Você cria e divulga um código só. O cliente usa na unidade que for mais perto dele, sem precisar saber qual loja é qual.',
  },
  {
    icon: <HubIcon />,
    titulo: 'Uma vez por cliente na rede inteira',
    texto:
      'Usou em uma loja, queimou nas outras. É a diferença entre uma campanha com custo previsível e uma que se multiplica pelo número de unidades.',
  },
  {
    icon: <StorefrontIcon />,
    titulo: 'Teto de uso por loja',
    texto:
      'Define quanto da campanha cada unidade pode consumir. A loja de maior movimento não leva a verba inteira nos primeiros dias.',
  },
  {
    icon: <TuneIcon />,
    titulo: 'A rede é sua para montar',
    texto:
      'Você marca quais lojas do grupo participam e tira quando quiser. Loja nova entra na campanha sem recriar nada.',
  },
];

const PASSOS: { titulo: string; texto: string }[] = [
  {
    titulo: 'Conectamos suas empresas',
    texto: 'Nossa equipe vincula as unidades do grupo à sua matriz no Sischef. É a única etapa que depende da gente.',
  },
  {
    titulo: 'Você monta a rede',
    texto: 'Nesta mesma tela, dá nome à rede e marca as lojas que participam. Leva um minuto.',
  },
  {
    titulo: 'Cria o cupom de rede',
    texto:
      'Na tela de cupons aparece a opção "vale em todas as lojas da rede", com uso único por cliente e teto por loja.',
  },
];

/**
 * Tela da "Rede de lojas" para empresa que ainda não tem o módulo de franquia.
 *
 * É comercial de propósito: o menu é visível para todo mundo justamente para
 * que quem tem outras lojas fora do Sischef descubra que dá para trazê-las.
 * Ver `docs/specs/cupom-rede.md` §7.
 */
export function ApresentacaoRede(): JSX.Element {
  const tema = useTheme();
  const escuro = tema.palette.mode === 'dark';
  const [solicitar, status] = useAdminSolicitarAcessoMutation();
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const acento = tema.palette.primary.main;

  async function aoSolicitar(): Promise<void> {
    setErro(null);
    try {
      await solicitar({ role: ROLE_FRANQUEADOR }).unwrap();
      setEnviado(true);
    } catch (err) {
      setErro(
        (err as { data?: { mensagem?: string } })?.data?.mensagem ??
          'Não foi possível enviar a solicitação. Tente novamente.',
      );
    }
  }

  const botaoCta = (
    <Stack spacing={1.5}>
      {enviado ? (
        <Alert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />}>
          Solicitação enviada. Nossa equipe entra em contato para entender o seu grupo de lojas.
        </Alert>
      ) : (
        <>
          {erro && <Alert severity="error">{erro}</Alert>}
          <Box>
            <Button
              variant="contained"
              size="large"
              onClick={aoSolicitar}
              disabled={status.isLoading}
              startIcon={status.isLoading ? <CircularProgress size={18} color="inherit" /> : <RedeemIcon />}
              fullWidth
              sx={{ width: { xs: '100%', sm: 'auto' }, px: { sm: 4 } }}
            >
              Quero ativar a rede de lojas
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Sem compromisso — a solicitação só avisa nosso time comercial.
          </Typography>
        </>
      )}
    </Stack>
  );

  return (
    <Box>
      {/* ---------------------------------------------------------------- HERO */}
      <AoEntrar atraso={0}>
        <PainelDestaque>
          <Stack spacing={2} sx={{ maxWidth: 640 }}>
            <Chip
              size="small"
              icon={<HubIcon />}
              label="Para redes e franquias"
              sx={{
                alignSelf: 'flex-start',
                bgcolor: alpha(acento, escuro ? 0.18 : 0.1),
                color: acento,
                fontWeight: 600,
                '& .MuiChip-icon': { color: acento },
              }}
            />
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: { xs: 26, md: 34 } }}>
              Uma promoção.
              <br />
              Todas as suas lojas.
              <Box component="span" sx={{ color: acento }}>
                {' '}
                Um uso por cliente.
              </Box>
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: { md: 17 } }}>
              A <strong>Rede de lojas</strong> conecta as unidades do seu grupo dentro do Cardápio Sischef.
              Com ela, um cupom de desconto vale em todas as lojas da rede — e cada cliente só consegue usar{' '}
              <strong>uma vez</strong>, não uma vez em cada unidade.
            </Typography>
            <Box sx={{ pt: 1 }}>{botaoCta}</Box>
          </Stack>
        </PainelDestaque>
      </AoEntrar>

      {/* -------------------------------------------------------- A COMPARAÇÃO */}
      <AoEntrar atraso={140}>
        <Box sx={{ mt: { xs: 3, md: 4 } }}>
          <Typography variant="h6" gutterBottom>
            Por que isso importa
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Uma rede com {LOJAS_EXEMPLO} lojas lança um cupom de {formatarBRL(CUPOM_EXEMPLO)}.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
            <CartaoComparacao
              tom="neutro"
              icone={<ReportProblemOutlinedIcon />}
              titulo="Hoje, sem a rede"
              itens={[
                `${LOJAS_EXEMPLO} cupons para criar e manter, um em cada loja`,
                'O mesmo cliente pode usar o cupom em cada uma delas',
                'Cada loja controla o próprio limite — ninguém vê o total',
              ]}
              destaque={{
                valor: formatarBRL(CUPOM_EXEMPLO * LOJAS_EXEMPLO),
                rotulo: 'de desconto que um único cliente pode acumular',
              }}
            />
            <CartaoComparacao
              tom="acento"
              icone={<CheckCircleOutlineIcon />}
              titulo="Com a rede de lojas"
              itens={[
                '1 cupom só, válido em todas as lojas da rede',
                'Uma vez por cliente — na loja que ele escolher',
                'Limite total da campanha e teto por loja, num lugar só',
              ]}
              destaque={{
                valor: formatarBRL(CUPOM_EXEMPLO),
                rotulo: 'é o teto por cliente, do jeito que você planejou',
              }}
            />
          </Box>
        </Box>
      </AoEntrar>

      {/* ---------------------------------------------------------- VANTAGENS */}
      <AoEntrar atraso={280}>
        <Box sx={{ mt: { xs: 3, md: 5 } }}>
          <Typography variant="h6" gutterBottom>
            O que você ganha
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Grade>
              {VANTAGENS.map((v) => (
                <CartaoPonto key={v.titulo} icon={v.icon} titulo={v.titulo} texto={v.texto} />
              ))}
            </Grade>
          </Box>
        </Box>
      </AoEntrar>

      {/* ------------------------------------------------------ COMO FUNCIONA */}
      <AoEntrar atraso={420}>
        <Box sx={{ mt: { xs: 3, md: 5 } }}>
          <Typography variant="h6" gutterBottom>
            Como fica funcionando
          </Typography>
          <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, mt: 2 }}>
            <Stack spacing={{ xs: 2.5, md: 3 }}>
              {PASSOS.map((p, i) => (
                <Stack key={p.titulo} direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      fontWeight: 700,
                      fontSize: 14,
                      color: acento,
                      border: `1px solid ${alpha(acento, 0.4)}`,
                      bgcolor: alpha(acento, escuro ? 0.14 : 0.06),
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Box>
                    <Typography fontWeight={600}>{p.titulo}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {p.texto}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Box>
      </AoEntrar>

      {/* ---------------------------------------------------------- CTA FINAL */}
      <AoEntrar atraso={560}>
        <Paper
          variant="outlined"
          sx={{
            mt: { xs: 3, md: 5 },
            mb: 2,
            p: { xs: 3, md: 4 },
            borderColor: alpha(acento, 0.35),
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack spacing={0.5} sx={{ maxWidth: 560 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <InsightsIcon sx={{ color: acento }} />
                <Typography variant="h6">Tem lojas fora do Sischef?</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                A rede funciona com as unidades do grupo dentro da mesma conta. Se parte das suas lojas ainda
                está fora, é justamente aqui que vale conversar — trazemos todas para o mesmo cardápio.
              </Typography>
            </Stack>
            <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}>{botaoCta}</Box>
          </Stack>
        </Paper>
      </AoEntrar>

      <Divider sx={{ mb: 2 }} />
      <Typography variant="caption" color="text.secondary">
        O recurso é liberado para <strong>quem administra a franquia</strong> — a matriz do grupo ou a
        empresa com o módulo de gestão de franquias —, que passa a montar a rede por conta própria.
      </Typography>
    </Box>
  );
}

function CartaoComparacao({
  tom,
  icone,
  titulo,
  itens,
  destaque,
}: {
  tom: 'neutro' | 'acento';
  icone: JSX.Element;
  titulo: string;
  itens: string[];
  destaque: { valor: string; rotulo: string };
}): JSX.Element {
  const tema = useTheme();
  const escuro = tema.palette.mode === 'dark';
  const acento = tema.palette.primary.main;
  const ehAcento = tom === 'acento';
  const cor = ehAcento ? acento : tema.palette.text.secondary;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2.5, md: 3 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(ehAcento ? { borderColor: alpha(acento, 0.35) } : {}),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: cor, mb: 1.5 }}>
        {icone}
        <Typography fontWeight={600} color="text.primary">
          {titulo}
        </Typography>
      </Stack>

      <Stack spacing={1} sx={{ flexGrow: 1 }}>
        {itens.map((t) => (
          <Stack key={t} direction="row" spacing={1} alignItems="flex-start">
            <Box
              sx={{
                mt: '7px',
                flexShrink: 0,
                width: 5,
                height: 5,
                borderRadius: '50%',
                bgcolor: ehAcento ? acento : 'text.disabled',
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {t}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Box
        sx={{
          mt: 2.5,
          pt: 2,
          borderTop: `1px solid ${tema.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 28, md: 32 },
            fontWeight: 700,
            lineHeight: 1.1,
            color: ehAcento ? acento : 'text.primary',
            // No cartão neutro o número é o "problema": fica sóbrio, sem alarde.
            opacity: ehAcento ? 1 : escuro ? 0.75 : 0.68,
          }}
        >
          {destaque.valor}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {destaque.rotulo}
        </Typography>
      </Box>
    </Paper>
  );
}

